import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  MainContext,
  STATUS_OFFLINE,
  STATUS_ONLINE_NOCONNECTION,
  STATUS_ONLINE,
  UIContext,
  UpdateContext,
} from "../../main-context";
import { checkExistsInDatabase, get } from "../../lib/comm.js";
import { useHistory, useParams } from "react-router-dom";
import {
  getInspectionUrl,
  INSPECTION_URL_TYPE_API,
  maintenanceCompareFn,
} from "../../lib/calculation";
import Maintenance from "./Maintenance";
import DailyWeekly from "./DailyWeekly";
import SafetyObservation from "./SafetyObservation";

export default function Inspection() {
  const update_context = useContext(UpdateContext);
  const main_context = useContext(MainContext);
  const ui_context = useContext(UIContext);
  const history = useHistory();
  const [selectedTerminal, setSelectedTerminal] = useState("");
  const [selectedEquipmentType, setSelectedEquipmentType] = useState("");
  const [currentMonth, setCurrentMonth] = useState();
  const params = useParams();
  const [selectedAirline, setSelectedAirline] = useState("");
  const [loaded, setLoaded] = useState(false);
  const inspection_types = useRef([
    "quarterly",
    "annual-semi-annual",
    "monthly",
    "calibrations",
  ]);

  const mx_types = useRef([
    "quarterly",
    "monthly",
    "annual",
    "semi-annual",
    "interior",
    "meter-calibration",
    "differential-pressure",
  ]);

  useEffect(() => {
    update_context.update_main_context((prev) => ({
      ...prev,
      inspections: null,
    }));
  }, [update_context]);

  useEffect(() => {
    // update_context.update_main_context((prev) => ({
    //   ...prev,
    //   inspections: null,
    // }));

    async function call_api() {
      let inspections = await get(
        "/api/inspections/" + params.type,
        null,
        main_context.status.id,
        update_context,
        history
      );

      if (inspections === "comm error") return;

      if (params.type === "fso") {
        setCurrentMonth(inspections.currentMonth);
      }
      const p = [];
      if (
        main_context.status.preferences.operationId ||
        params.type === "fso"
      ) {
        const valid_keys =
          params.type !== "maintenance" ? [params.type] : mx_types.current;
        for (const [key, value] of Object.entries(inspections)) {
          if (valid_keys.includes(key)) {
            if (params.type !== "fso") {
              if (
                ui_context.online_status !== STATUS_ONLINE &&
                value.length &&
                (+value[0].vendorId !==
                  +main_context.status.preferences.vendorId ||
                  +value[0].stationId !==
                    +main_context.status.preferences.stationId)
              ) {
                update_context.update_ui_context((prev) => ({
                  ...prev,
                  error: "No offline data - not possible to proceed!",
                }));
                return;
              }
              const ops_numbers =
                main_context.status.preferences.operationId.map((x) => +x);
              inspections[key] = value.filter(
                (x) =>
                  ops_numbers.includes(+x.operationId) &&
                  +x.vendorId === +main_context.status.preferences.vendorId &&
                  +x.stationId === +main_context.status.preferences.stationId
              );
            }
            inspections[key].forEach((x) => {
              const url = getInspectionUrl(
                INSPECTION_URL_TYPE_API,
                x,
                null,
                params.type === "fso"
              );
              const inner_p = checkExistsInDatabase(url);
              inner_p.then((result) => {
                x.offline_exists = result === true;
              });
              p.push(inner_p);
            });
          }
        }
      }

      await Promise.all(p);

      if (params.type !== "maintenance") {
        update_context.update_main_context((prev) => ({
          ...prev,
          inspections: inspections,
        }));
      } else {
        const maintenance = inspections;
        maintenance["annual-semi-annual"] = inspections["annual"]
          .concat(inspections["semi-annual"])
          .concat(inspections["interior"]);

        maintenance["calibrations"] = inspections["meter-calibration"]
          .concat(inspections["differential-pressure"])
          .map(function (item) {
            if (item.abbreviation === "C") {
              item.abbreviation = "M";
            }

            return item;
          });

        maintenance["calibrations"].sort(maintenanceCompareFn);
        maintenance["annual-semi-annual"].sort(maintenanceCompareFn);
        update_context.update_main_context((prev) => ({
          ...prev,
          inspections: maintenance,
        }));
      }
    }

    setLoaded(true);
    call_api();
  }, [
    history,
    main_context.status.id,
    main_context.status.preferences.operationId,
    main_context.status.preferences.stationId,
    main_context.status.preferences.vendorId,
    params.type,
    ui_context.online_status,
    update_context,
  ]);

  const equipmentTypeFilter = useCallback(
    (inspection) => {
      if (selectedEquipmentType) {
        return "" + inspection.equipmentTypeId === "" + selectedEquipmentType;
      } else {
        return true;
      }
    },
    [selectedEquipmentType]
  );

  const airlineFilter = useCallback(
    (inspection) => {
      if (selectedAirline) {
        return inspection.airlines.includes(parseInt(selectedAirline));
      } else {
        return true;
      }
    },
    [selectedAirline]
  );

  const selectedTerminalFilter = useCallback(
    (inspection) => {
      if (!!selectedTerminal || undefined) {
        return "" + inspection.terminal === "" + selectedTerminal;
      } else {
        return true;
      }
    },
    [selectedTerminal]
  );

  const inspectionStatus = useCallback(
    (status, subtype) => {
      const type = params.type !== "maintenance" ? params.type : subtype;
      if (!main_context.inspections) return [];
      const inspections = main_context.inspections[type];
      if (!inspections) return [];

      var filtered = [];

      if (status === "offline_complete") {
        inspections.forEach(function (inspection) {
          if (inspection.offline_complete) {
            filtered.push(inspection);
          }
        });
      } else if (status === "complete") {
        inspections.forEach(function (inspection) {
          if (
            !inspection.offline_complete &&
            inspection.status !== 10 &&
            (inspection.closed || !inspection.dueThisPeriod)
          ) {
            filtered.push(inspection);
          }
        });
      } else if (status === "status_complete_obs_not_accepted") {
        inspections.forEach(function (inspection) {
          if (
            !inspection.offline_complete &&
            inspection.status !== 7 &&
            inspection.status !== 10 &&
            (inspection.closed || !inspection.dueThisPeriod)
          ) {
            filtered.push(inspection);
          }
        });
      } else if (status === "status_obs_accepted") {
        inspections.forEach(function (inspection) {
          if (!inspection.offline_complete && inspection.status === 7) {
            filtered.push(inspection);
          }
        });
      } else {
        inspections.forEach(function (inspection) {
          if (
            !inspection.offline_complete &&
            (inspection.status === 10 ||
              (!inspection.closed && inspection.dueThisPeriod))
          ) {
            filtered.push(inspection);
          }
        });
      }

      return filtered;
    },
    [main_context.inspections, params.type]
  );

  const inspectionFilter = useCallback(
    (status, subtype) => {
      return inspectionStatus(status, subtype).filter(
        (x) =>
          selectedTerminalFilter(x) &&
          airlineFilter(x) &&
          equipmentTypeFilter(x)
      );
    },
    [
      airlineFilter,
      equipmentTypeFilter,
      inspectionStatus,
      selectedTerminalFilter,
    ]
  );

  const existsInDatabase = useCallback(
    (inspection) => {
      return ui_context.online_status === STATUS_OFFLINE ||
        ui_context.online_status === STATUS_ONLINE_NOCONNECTION
        ? inspection.offline_exists
        : true;
    },
    [ui_context.online_status]
  );

  return (
    <div className="layout-admin">
      <div className="admin-content" id="admin-content">
        <div className="container-fluid">
          <div className="row row-admin-content">
            <div className="col-xs-12">
              {loaded && (
                <>
                  {params.type === "maintenance" && (
                    <Maintenance
                      setSelectedAirline={setSelectedAirline}
                      setSelectedTerminal={setSelectedTerminal}
                      setSelectedEquipmentType={setSelectedEquipmentType}
                      inspectionFilter={inspectionFilter}
                      inspection_types={inspection_types}
                      existsInDatabase={existsInDatabase}
                      selectedTerminal={selectedTerminal}
                      selectedEquipmentType={selectedEquipmentType}
                    ></Maintenance>
                  )}
                  {params.type === "fso" && (
                    <SafetyObservation
                      currentMonth={currentMonth}
                      inspectionFilter={inspectionFilter}
                      existsInDatabase={existsInDatabase}
                    ></SafetyObservation>
                  )}
                  {(params.type === "daily" || params.type === "weekly") && (
                    <DailyWeekly
                      setSelectedAirline={setSelectedAirline}
                      setSelectedTerminal={setSelectedTerminal}
                      inspectionFilter={inspectionFilter}
                      existsInDatabase={existsInDatabase}
                      params_type={params.type}
                      selectedTerminal={selectedTerminal}
                    ></DailyWeekly>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
